import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/terapeutas/')({ component: AdminTherapistsIndex });

const MODALITY_LABEL: Record<string, string> = {
  ONLINE: 'Online', PRESENCIAL: 'Presencial', IN_PERSON: 'Presencial', BOTH: 'Híbrido', HIBRID: 'Híbrido', HYBRID: 'Híbrido',
};

function AdminTherapistsIndex() {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');

  const { data = [], isLoading } = useQuery({
    queryKey: ['admin-therapists'],
    queryFn: () => api.get('admin/therapists').json<any[]>(),
  });

  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    return data.filter((t: any) =>
      [t.user?.name, t.user?.email, t.crpNumber].filter(Boolean).some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [data, query]);

  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('auth/me').json<{ id: string; role: string }>(),
  });

  const toggleVerify = useMutation({
    mutationFn: ({ id, verified }: { id: string; verified: boolean }) =>
      api.patch(`admin/therapists/${id}/verify`, { json: { verified } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapists'] }),
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`admin/therapists/${id}/active`, { json: { isActive } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapists'] }),
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: 'ADMIN' | 'THERAPIST' }) =>
      api.patch(`admin/therapists/${id}/role`, { json: { role } }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-therapists'] }),
  });

  return (
    <>
      <AdminPageHeader title="Profissionais" subtitle={`${data.length} cadastrados`} />

      <input
        type="search"
        placeholder="Buscar por nome, e-mail ou CRP..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="max-w-md w-full bg-white border border-slate-200 rounded-md h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 mb-4"
      />

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50">
              <tr>
                <Th>Profissional</Th>
                <Th>Tipo · Plano</Th>
                <Th>Modalidade</Th>
                <Th>Status</Th>
                <Th align="right">Ações</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Carregando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-12 text-center text-sm text-slate-400">Nenhum profissional encontrado.</td></tr>
              ) : filtered.map((t: any) => {
                const avatar = t.avatarUrl ?? t.user?.image;
                return (
                  <tr key={t.id} className="hover:bg-slate-50/60">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {avatar ? (
                          <img src={avatar} alt="" className="size-9 rounded-full object-cover ring-2 ring-teal-100 shrink-0" />
                        ) : (
                          <div className="size-9 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
                            {(t.user?.name ?? '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 leading-tight truncate">{t.user?.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{t.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-slate-500">PJ</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 w-fit">{t.plan}</span>
                        {t.user?.isMaster ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 w-fit font-semibold">★ Master</span>
                        ) : t.user?.role === 'ADMIN' ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 w-fit">★ Admin</span>
                        ) : null}
                        {t.crpNumber && <span className="text-[10px] text-slate-400">CRP {t.crpNumber}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {MODALITY_LABEL[t.modality] ?? t.modality ?? '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={cn(
                          'text-[10px] px-2 py-0.5 rounded border w-fit',
                          t.isActive ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-red-50 text-red-600 border-red-200',
                        )}>
                          {t.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                        <span className={cn(
                          'text-[10px] px-2 py-0.5 rounded border w-fit',
                          t.isFiscalDocVerified ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-amber-50 text-amber-700 border-amber-200',
                        )}>
                          {t.isFiscalDocVerified ? '✓ Verificado' : 'Aguarda verif.'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2.5"
                          onClick={() => toggleVerify.mutate({ id: t.id, verified: !t.isFiscalDocVerified })}
                          disabled={toggleVerify.isPending}
                        >
                          {t.isFiscalDocVerified ? 'Desverif.' : 'Verificar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2.5"
                          onClick={() => toggleActive.mutate({ id: t.id, isActive: !t.isActive })}
                          disabled={toggleActive.isPending}
                        >
                          {t.isActive ? 'Suspender' : 'Reativar'}
                        </Button>
                        {(() => {
                          const isSelf = me?.id === t.user?.id;
                          const isMaster = !!t.user?.isMaster;
                          const isAdmin = t.user?.role === 'ADMIN';
                          const blocked = isSelf || isMaster;
                          const title = isMaster
                            ? 'Master admin (ADMIN_EMAILS) — não pode ser alterado'
                            : isSelf
                              ? 'Você não pode alterar sua própria role'
                              : '';
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className={cn('h-7 text-xs px-2.5', isAdmin ? 'text-slate-600' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50')}
                              onClick={() => setRole.mutate({ id: t.id, role: isAdmin ? 'THERAPIST' : 'ADMIN' })}
                              disabled={setRole.isPending || blocked}
                              title={title}
                            >
                              {isAdmin ? '↓ Remover admin' : '★ Tornar admin'}
                            </Button>
                          );
                        })()}
                        <Link
                          to="/admin/terapeutas/$id"
                          params={{ id: t.id }}
                          className="text-[11px] font-semibold text-teal-600 hover:underline px-1"
                        >
                          Revisar →
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`text-${align} px-5 py-3 text-slate-500 font-semibold uppercase text-xs`}>{children}</th>;
}
